import { useCallback, useEffect, useRef, useState } from "react";
import { subscribeToPush, unsubscribeFromPush } from "./services/pushService";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";

// --- IMPORTS ADMIN UNIQUEMENT ---
import AdminLayout from "./components/AdminLayout";
import { FournisseurDroits } from "./context/Droits";
import PageIntrouvable from "./components/PageIntrouvable";
import AdminLogin from "./pages/login";
import CompleteProfile from "./pages/complete-profile";
import Dashboard from "./pages/dashboard";
import Referentiel from "./pages/referentiel";
import Diagnostics from "./pages/diagnostic";
import Users from "./pages/users";
import OrgDetails from "./pages/org-details";
import Admins from "./pages/admins";
import Payments from "./pages/payments";
import Settings from "./pages/settings";
import Profile from "./pages/profile";
import Messages from "./pages/messages";
import Projets from "./pages/projets";
import ProjetDetail from "./pages/projet-detail";
import Coaches from "./pages/coaches";
import CandidatureDetail from "./pages/candidature-detail";
import FormationAdmin from "./pages/formation-admin";
import KitAdmin from "./pages/kit-admin";
import Help from "./pages/help";
import Demandes from "./pages/demandes";
import Cms from "./pages/cms";
import EspaceCoach from "./pages/espace-coach";
import MesFormations from "./pages/mes-formations";
import PrivateAdminRoute from "./routes/PrivateAdminRoute";
import Activation from "./pages/activation";
import AccueilSelonRole from "./components/AccueilSelonRole";
import MotDePasseOublie from "./pages/mot-de-passe-oublie";
import { adminApi } from "./services/api";
import { annoncer } from "./etats";

// --- PARAMETRES SESSION ---
// Deconnexion auto apres 30 minutes d'inactivite.
const ADMIN_INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const ADMIN_LAST_ACTIVITY_KEY = "asso_admin_last_activity";
const ADMIN_ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const inactivityTimeoutRef = useRef(null);
  const inactivityThrottleRef = useRef(null);
  const inactivityWarningRef = useRef(null);
  const recordActivityRef = useRef(null);

  // --- ETAT SESSION ADMIN ---
  const [adminUser, setAdminUser] = useState(() => {
    const saved = localStorage.getItem("asso_admin_user");
    return saved ? JSON.parse(saved) : null;
  });

  // --- ETAT THEME ADMIN ---
  const [adminTheme, setAdminTheme] = useState(() => {
    if (adminUser) return adminApi.getThemePreference(adminUser.id);
    return "light";
  });

  // --- ACTIONS AUTHENTIFICATION ---
  const handleAdminLogin = (data) => {
    localStorage.setItem("asso_admin_user", JSON.stringify(data));
    localStorage.setItem(ADMIN_LAST_ACTIVITY_KEY, String(Date.now()));
    setAdminUser(data);
    setAdminTheme(adminApi.getThemePreference(data.id) || "light");
    subscribeToPush();

    // Revenu à la connexion après une session expirée (système d'états) :
    // on retourne à la page quittée. Seule une adresse interne est suivie.
    const retour = new URLSearchParams(window.location.search).get("retour");
    const retourSur = retour && retour.startsWith("/") && !retour.startsWith("//")
      && !retour.startsWith("/login");

    if (data.first_login) {
      navigate("/complete-profile", { replace: true });
    } else if (retourSur) {
      navigate(retour, { replace: true });
    } else {
      // La racine choisit la page d'arrivée selon les zones du compte : un
      // coach n'a pas accès au tableau de bord et y lisait un message d'erreur.
      navigate("/", { replace: true });
    }
  };

  const handleCompleteProfile = (data) => {
    localStorage.setItem("asso_admin_user", JSON.stringify(data));
    localStorage.setItem(ADMIN_LAST_ACTIVITY_KEY, String(Date.now()));
    setAdminUser(data);
    setAdminTheme(adminApi.getThemePreference(data.id) || "light");
    navigate("/", { replace: true });
  };

  const handleAdminLogout = useCallback((reason = "") => {
    unsubscribeFromPush();
    if (inactivityWarningRef.current) {
      clearTimeout(inactivityWarningRef.current);
      inactivityWarningRef.current = null;
    }
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
    if (inactivityThrottleRef.current) {
      clearTimeout(inactivityThrottleRef.current);
      inactivityThrottleRef.current = null;
    }

    // La session est coupee AVANT tout affichage. `window.alert` bloquait le
    // fil d'execution : le message s'affichait alors que la console etait
    // encore ouverte derriere, et la redirection n'avait lieu qu'apres un clic.
    // Desormais on efface, on redirige, et le motif s'affiche sur le
    // formulaire de connexion.
    //
    // Les jetons JWT sont effaces eux aussi. Ils ne l'etaient pas : apres une
    // « deconnexion », l'acces restait valide une heure et renouvelable sept
    // jours depuis le navigateur.
    localStorage.removeItem("asso_admin_user");
    localStorage.removeItem(ADMIN_LAST_ACTIVITY_KEY);
    localStorage.removeItem("asso_acc_token");
    localStorage.removeItem("asso_acc_refresh");
    setAdminUser(null);

    // `replace` : la page protegee quittee ne doit pas revenir avec le bouton
    // Precedent du navigateur.
    navigate("/login", { replace: true, state: reason ? { motif: reason } : undefined });
  }, [navigate]);

  // Re-subscribe on page reload if session already exists
  useEffect(() => {
    if (adminUser && !adminUser.first_login) subscribeToPush();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- DECONNEXION AUTO INACTIVITE ---
  useEffect(() => {
    if (!adminUser || adminUser.first_login) return undefined;

    const scheduleAutoLogout = () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }

      inactivityTimeoutRef.current = setTimeout(() => {
        handleAdminLogout("Vous avez ete deconnecte apres 30 minutes d'inactivite.");
      }, ADMIN_INACTIVITY_TIMEOUT_MS);

      // Session sur le point d'expirer : on prévient une minute avant, avec
      // de quoi rester connecté, plutôt que de couper une saisie en cours.
      if (inactivityWarningRef.current) clearTimeout(inactivityWarningRef.current);
      inactivityWarningRef.current = setTimeout(() => {
        annoncer("Sans activité de votre part, vous serez déconnecté dans une minute.", {
          ton: "attention", titre: "Session sur le point d'expirer", duree: 60000,
          action: { libelle: "Rester connecté", onClick: () => recordActivityRef.current?.() },
        });
      }, ADMIN_INACTIVITY_TIMEOUT_MS - 60 * 1000);
    };

    const recordActivity = () => {
      localStorage.setItem(ADMIN_LAST_ACTIVITY_KEY, String(Date.now()));
      scheduleAutoLogout();
    };
    recordActivityRef.current = recordActivity;

    const handleActivity = () => {
      if (inactivityThrottleRef.current) return;

      inactivityThrottleRef.current = setTimeout(() => {
        inactivityThrottleRef.current = null;
        recordActivity();
      }, 1000);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleActivity();
      }
    };

    const previousActivity = Number(localStorage.getItem(ADMIN_LAST_ACTIVITY_KEY) || 0);
    if (previousActivity > 0 && Date.now() - previousActivity >= ADMIN_INACTIVITY_TIMEOUT_MS) {
      handleAdminLogout("Votre session a expire. Reconnectez-vous pour continuer.");
      return undefined;
    }

    recordActivity();
    ADMIN_ACTIVITY_EVENTS.forEach((eventName) => window.addEventListener(eventName, handleActivity));
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      ADMIN_ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, handleActivity));
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      if (inactivityWarningRef.current) {
        clearTimeout(inactivityWarningRef.current);
        inactivityWarningRef.current = null;
      }

      if (inactivityThrottleRef.current) {
        clearTimeout(inactivityThrottleRef.current);
        inactivityThrottleRef.current = null;
      }
    };
  }, [adminUser, handleAdminLogout]);

  // --- ACTIONS PREFERENCES / PROFIL ---
  const handleSetTheme = (newTheme) => {
    setAdminTheme(newTheme);
    if (adminUser) {
      adminApi.setThemePreference(adminUser.id, newTheme);
    }
  };

  const handleAdminProfileUpdate = (updatedData) => {
    if (!adminUser) return;

    const mergedUser = { ...adminUser, ...updatedData };
    localStorage.setItem("asso_admin_user", JSON.stringify(mergedUser));
    setAdminUser(mergedUser);
  };

  // --- ROUTAGE ADMIN ---
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname.split("/")[1] || "root"}>
        {/* Route publique de connexion */}
        <Route
          path="/login"
          element={
            adminUser && !adminUser.first_login ? (
              <AccueilSelonRole />
            ) : adminUser?.first_login ? (
              <Navigate to="/complete-profile" replace />
            ) : (
              <AdminLogin onLogin={handleAdminLogin} />
            )
          }
        />

        {/* Parcours d'acces, publics par nature : la personne qui les ouvre
            n'a precisement pas encore de session. Ils restent accessibles
            meme connecte, pour qu'un lien recu par e-mail ne renvoie pas au
            tableau de bord sans explication. */}
        <Route path="/activation" element={<Activation onLogin={handleAdminLogin} />} />
        <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />

        {/* Premiere connexion: completion profil */}
        <Route
          path="/complete-profile"
          element={
            adminUser?.first_login ? (
              <CompleteProfile user={adminUser} onComplete={handleCompleteProfile} />
            ) : adminUser ? (
              <AccueilSelonRole />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Console privee admin */}
        <Route
          element={
            /* Les droits sont chargés une fois, au-dessus de la coque :
               le menu, le garde de route et les boutons d'export lisent
               la même réponse au lieu de l'interroger chacun. */
            <PrivateAdminRoute user={adminUser}>
              <FournisseurDroits>
                <AdminLayout user={adminUser} theme={adminTheme} onSetTheme={handleSetTheme} onLogout={handleAdminLogout} />
              </FournisseurDroits>
            </PrivateAdminRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard user={adminUser} />} />
          <Route path="/referentiel" element={<Referentiel user={adminUser} />} />
          <Route path="/diagnostics" element={<Diagnostics user={adminUser} />} />
          <Route path="/users" element={<Users />} />
          <Route path="/users/:id" element={<OrgDetails />} />
          <Route path="/admins" element={<Admins user={adminUser} />} />
          <Route path="/payments" element={<Payments user={adminUser} />} />
          <Route path="/messages" element={<Messages user={adminUser} />} />
          {/* Module Accompagnement */}
          <Route path="/projets" element={<Projets user={adminUser} />} />
          <Route path="/projets/:id" element={<ProjetDetail user={adminUser} />} />
          <Route path="/espace-coach" element={<EspaceCoach user={adminUser} />} />
          <Route path="/mes-formations" element={<MesFormations />} />
          <Route path="/coaches" element={<Coaches user={adminUser} />} />
          <Route path="/coaches/candidatures/:id" element={<CandidatureDetail user={adminUser} />} />
          <Route path="/formation-admin" element={<FormationAdmin user={adminUser} />} />
          <Route path="/kit-admin" element={<KitAdmin user={adminUser} />} />
          <Route path="/demandes" element={<Demandes user={adminUser} />} />
          {/* Gestion des contenus. Le composant refuse lui-meme l'affichage a
              un role autre que SUPER_ADMIN, et l'API applique la meme regle :
              masquer l'entree de menu ne protegerait rien. */}
          <Route path="/cms" element={<Cms user={adminUser} />} />
          <Route path="/help" element={<Help />} />
          {/* Deux pages, deux objets. « Mon profil » porte l'identite et le
              mot de passe, « Parametres » les reglages de la console. Elles
              n'ont besoin d'aucune zone : tout compte du back-office a un
              profil et des reglages, il n'y a donc rien a habiliter. */}
          <Route
            path="/profile"
            element={
              <Profile
                user={adminUser}
                onProfileUpdated={handleAdminProfileUpdate}
              />
            }
          />
          <Route
            path="/settings"
            element={<Settings user={adminUser} theme={adminTheme} onSetTheme={handleSetTheme} />}
          />

          {/* Racine : première zone que le serveur déclare accessible
              (tableau de bord pour l'équipe, projets pour un coach). */}
          <Route path="/" element={<AccueilSelonRole />} />

          {/* Adresse inconnue, pour un compte connecté. Dans le cadre, donc
              avec le menu et l'en-tête : `AdminLayout` n'y reconnaît aucune
              zone et affiche « Page introuvable » — le même écran qu'une
              zone fermée. Elle renvoyait auparavant au tableau de bord, ce
              qui distinguait les deux cas : il suffisait de comparer pour
              savoir quelles pages existent sans y avoir droit. */}
          <Route path="*" element={<PageIntrouvable />} />
        </Route>

        {/* Ce qui reste ici n'est pas une adresse inconnue mais un parcours
            inachevé : pas de session, ou une première connexion à terminer.
            Les adresses inconnues d'un compte connecté sont traitées dans le
            cadre, ci-dessus. */}
        <Route
          path="*"
          element={
            adminUser?.first_login
              ? <Navigate to="/complete-profile" replace />
              : <Navigate to="/login" replace />
          }
        />
      </Routes>
    </AnimatePresence>
  );
}
